import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import api, { formatError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Camera, Plus, Trash2, User as UserIcon, LogOut, Minus } from "lucide-react";

const SPORTS = [
    "Grassroots Football",
    "Amateur Football",
    "Youth Football U14-U19",
    "Futsal",
    "Mini Football",
    "Padel",
    "Basketball",
    "Tennis",
];

const PAYMENT_METHODS = [
    "ContactPay (Eurobank)",
    "QuickPay (Bank of Cyprus)",
    "Revolut",
    "IBAN transfer",
    "Cash",
];

export default function Profile() {
    const { user, refresh, logout } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const [sports, setSports] = useState([]);
    const [savingSports, setSavingSports] = useState(false);
    const [payments, setPayments] = useState([]);
    const [savingPayments, setSavingPayments] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [birthYear, setBirthYear] = useState("");
    const [location, setLocation] = useState("");
    const [savingPersonal, setSavingPersonal] = useState(false);

    useEffect(() => {
        if (user?.sports_experience?.length) {
            setSports(user.sports_experience);
        }
        if (user?.payment_methods?.length) {
            setPayments(user.payment_methods);
        }
        if (user) {
            setBirthYear(user.birth_year ? String(user.birth_year) : "");
            setLocation(user.location || "");
        }
    }, [user]);

    if (!user) return null;

    const avatarUrl = user.avatar_path
        ? `${API_BASE}/files/${user.avatar_path}?auth=${token}&v=${user.avatar_path}`
        : null;

    const onAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(t("profile.photoUpdated"));
            await refresh();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
        }
    };

    const addSport = () => setSports([...sports, { sport: "", years: "" }]);
    const removeSport = (i) => setSports(sports.filter((_, idx) => idx !== i));
    const updateSport = (i, key, val) => {
        const next = [...sports];
        next[i] = { ...next[i], [key]: val };
        setSports(next);
    };


    const savePersonal = async () => {
        const yearNum = birthYear ? parseInt(birthYear, 10) : null;
        const currentYear = new Date().getFullYear();
        if (birthYear && (Number.isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear)) {
            toast.error(t("profile.yearRangeError", { max: currentYear }));
            return;
        }
        setSavingPersonal(true);
        try {
            await api.put("/profile/personal", { birth_year: yearNum, location });
            toast.success(t("profile.detailsSaved"));
            await refresh();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingPersonal(false);
        }
    };

    const saveSports = async () => {
        const seen = new Set();
        for (const s of sports) {
            if (!s.sport) {
                toast.error(t("profile.selectSportError"));
                return;
            }
            if (seen.has(s.sport)) {
                toast.error(t("profile.duplicateSport", { name: s.sport }));
                return;
            }
            seen.add(s.sport);
        }
        const cleanSports = sports.map((s) => ({ sport: s.sport, years: String(s.years ?? "").trim() }));
        setSavingSports(true);
        try {
            await api.put("/profile/sports", { sports: cleanSports });
            toast.success(t("profile.sportsSaved"));
            await refresh();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingSports(false);
        }
    };

    const addPayment = () => setPayments([...payments, { method: "", details: "" }]);
    const removePayment = (i) => setPayments(payments.filter((_, idx) => idx !== i));
    const updatePayment = (i, key, val) => {
        const next = [...payments];
        next[i] = { ...next[i], [key]: val };
        setPayments(next);
    };

    const savePayments = async () => {
        const seen = new Set();
        for (const p of payments) {
            if (!p.method) {
                toast.error(t("profile.selectMethodError"));
                return;
            }
            if (seen.has(p.method)) {
                toast.error(t("profile.duplicateMethod", { name: p.method }));
                return;
            }
            seen.add(p.method);
        }
        const cleanPayments = payments.map((p) => ({ method: p.method, details: String(p.details ?? "").trim() }));
        setSavingPayments(true);
        try {
            await api.put("/profile/payments", { payments: cleanPayments });
            toast.success(t("profile.paymentsSaved"));
            await refresh();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingPayments(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="glass sticky top-0 z-20 border-b border-border">
                <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
                    <Link to="/dashboard" className="label-mono flex items-center gap-2 hover:text-primary transition-colors min-w-0 truncate" data-testid="profile-back-link">
                        <ArrowLeft className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{t("profile.back")}</span>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        <LanguageSwitcher variant="compact" />
                        <Button variant="outline" size="sm" onClick={logout} className="rounded-md flex-shrink-0" data-testid="profile-logout-btn">
                            <LogOut className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{t("common.signOut")}</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-8 py-12 space-y-10">
                <div>
                    <div className="label-mono mb-2">{t("profile.kicker")}</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{t("profile.title")}</h1>
                </div>

                <section className="bg-white border border-border rounded-md p-8" data-testid="profile-photo-section">
                    <h2 className="font-heading text-lg font-medium mb-6">{t("profile.photoSection")}</h2>
                    <div className="flex items-center gap-6">
                        <div className="relative w-24 h-24 rounded-full bg-accent border border-border overflow-hidden flex items-center justify-center" data-testid="profile-avatar-preview">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                                <UserIcon className="w-10 h-10 text-muted-foreground" />
                            )}
                        </div>
                        <div className="space-y-2">
                            <div className="font-medium" data-testid="profile-username">{user.name}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input type="file" accept="image/*" className="hidden" onChange={onAvatarChange} data-testid="profile-photo-input" />
                                <span className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors">
                                    <Camera className="w-4 h-4" />
                                    {uploading ? t("profile.uploading") : avatarUrl ? t("profile.changePhoto") : t("profile.uploadPhoto")}
                                </span>
                            </label>
                        </div>
                    </div>
                </section>

                <section className="bg-white border border-border rounded-md p-8" data-testid="profile-personal-section">
                    <h2 className="font-heading text-lg font-medium mb-1">{t("profile.personalSection")}</h2>
                    <p className="text-sm text-muted-foreground mb-6">{t("profile.personalSub")}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="birth-year">{t("profile.birthYear")}</Label>
                            <Input id="birth-year" type="number" min="1900" max={new Date().getFullYear()} placeholder={t("profile.birthYearPlaceholder")}
                                value={birthYear} onChange={(e) => setBirthYear(e.target.value)}
                                className="h-11 rounded-md mono" data-testid="profile-birth-year-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="location">{t("profile.location")}</Label>
                            <Input id="location" placeholder={t("profile.locationPlaceholder")}
                                value={location} onChange={(e) => setLocation(e.target.value)}
                                className="h-11 rounded-md" data-testid="profile-location-input" />
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <Button onClick={savePersonal} disabled={savingPersonal}
                            className="h-11 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-8"
                            data-testid="profile-save-personal-btn">
                            {savingPersonal ? t("common.saving") : t("profile.saveDetails")}
                        </Button>
                    </div>
                </section>

                <section className="bg-white border border-border rounded-md p-8" data-testid="profile-sports-section">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="font-heading text-lg font-medium">{t("profile.sportsSection")}</h2>
                            <p className="text-sm text-muted-foreground mt-1">{t("profile.sportsSub")}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={addSport} className="rounded-md" data-testid="profile-add-sport-btn">
                            <Plus className="w-4 h-4 mr-1" /> {t("profile.addSport")}
                        </Button>
                    </div>

                    {sports.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="profile-sports-empty">
                            {t("profile.noSports")}
                        </p>
                    ) : (
                        <ul className="space-y-3" data-testid="profile-sports-list">
                            {sports.map((s, i) => (
                                <li key={i} className="grid grid-cols-12 gap-3 items-end" data-testid={`profile-sport-row-${i}`}>
                                    <div className="col-span-7 space-y-1.5">
                                        {i === 0 && <Label className="text-xs">{t("profile.sport")}</Label>}
                                        <Select value={s.sport} onValueChange={(v) => updateSport(i, "sport", v)}>
                                            <SelectTrigger className="h-11 rounded-md" data-testid={`profile-sport-select-${i}`}>
                                                <SelectValue placeholder={t("profile.selectSportPlaceholder")} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SPORTS.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-4 space-y-1.5">
                                        {i === 0 && <Label className="text-xs">{t("profile.years")}</Label>}
                                        <Input
                                            type="text"
                                            value={s.years}
                                            placeholder={t("profile.yearsPlaceholder")}
                                            maxLength={40}
                                            onChange={(e) => updateSport(i, "years", e.target.value)}
                                            className="h-11 rounded-md"
                                            data-testid={`profile-sport-years-${i}`}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Button type="button" variant="ghost" size="icon" onClick={() => removeSport(i)}
                                            className="h-11 w-11 text-muted-foreground hover:text-destructive"
                                            data-testid={`profile-sport-delete-${i}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-6 flex justify-end">
                        <Button onClick={saveSports} disabled={savingSports}
                            className="h-11 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-8"
                            data-testid="profile-save-sports-btn">
                            {savingSports ? t("common.saving") : t("profile.saveSports")}
                        </Button>
                    </div>
                </section>

                <section className="bg-white border border-border rounded-md p-8" data-testid="profile-payments-section">
                    <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                        <div>
                            <h2 className="font-heading text-lg font-medium">{t("profile.paymentsSection")}</h2>
                            <p className="text-sm text-muted-foreground mt-1">{t("profile.paymentsSub")}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={addPayment} className="rounded-md" data-testid="profile-add-payment-btn">
                            <Plus className="w-4 h-4 mr-1" /> {t("profile.addPaymentMethod")}
                        </Button>
                    </div>

                    {payments.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="profile-payments-empty">
                            {t("profile.noPayments")}
                        </p>
                    ) : (
                        <ul className="space-y-3" data-testid="profile-payments-list">
                            {payments.map((p, i) => (
                                <li key={i} className="grid grid-cols-12 gap-3 items-end" data-testid={`profile-payment-row-${i}`}>
                                    <div className="col-span-5 space-y-1.5">
                                        {i === 0 && <Label className="text-xs">{t("profile.paymentMethod")}</Label>}
                                        <Select value={p.method} onValueChange={(v) => updatePayment(i, "method", v)}>
                                            <SelectTrigger className="h-11 rounded-md" data-testid={`profile-payment-method-select-${i}`}>
                                                <SelectValue placeholder={t("profile.selectMethodPlaceholder")} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-6 space-y-1.5">
                                        {i === 0 && <Label className="text-xs">{t("profile.paymentDetails")}</Label>}
                                        <Input
                                            type="text"
                                            value={p.details}
                                            placeholder={t("profile.paymentDetailsPlaceholder")}
                                            maxLength={200}
                                            onChange={(e) => updatePayment(i, "details", e.target.value)}
                                            className="h-11 rounded-md"
                                            data-testid={`profile-payment-details-${i}`}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Button type="button" variant="ghost" size="icon" onClick={() => removePayment(i)}
                                            className="h-11 w-11 text-muted-foreground hover:text-destructive"
                                            data-testid={`profile-payment-delete-${i}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-6 flex justify-end">
                        <Button onClick={savePayments} disabled={savingPayments}
                            className="h-11 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-8"
                            data-testid="profile-save-payments-btn">
                            {savingPayments ? t("common.saving") : t("profile.savePayments")}
                        </Button>
                    </div>
                </section>
            </main>
        </div>
    );
}
